import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Paper,
  Breadcrumbs,
  Link,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { saveCategory, toSlug } from "../../utils/storage";

const AddCategory = () => {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    eyebrow: "",
    description: "",
  });
  const [slugManual, setSlugManual] = useState(false);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Auto-generate slug from name unless user edited slug manually
      if (name === "name" && !slugManual) {
        next.slug = toSlug(value);
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSlugChange = (e) => {
    setSlugManual(true);
    setForm((prev) => ({ ...prev, slug: e.target.value }));
    setErrors((prev) => ({ ...prev, slug: "" }));
  };

  const validate = () => {
    const temp = {};
    if (!form.name.trim()) temp.name = "Category name is required";
    else if (form.name.trim().length < 3)
      temp.name = "Minimum 3 characters required";

    if (!form.slug.trim()) temp.slug = "Slug is required";
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug.trim()))
      temp.slug = "Lowercase letters, numbers and hyphens only";

    if (!form.eyebrow.trim()) temp.eyebrow = "Eyebrow label is required";
    if (!form.description.trim()) temp.description = "Description is required";
    else if (form.description.trim().length < 10)
      temp.description = "Minimum 10 characters required";

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    saveCategory({
      name: form.name.trim(),
      slug: form.slug.trim(),
      eyebrow: form.eyebrow.trim(),
      description: form.description.trim(),
    });
    window.dispatchEvent(
      new Event("categoriesUpdated")
    );
    setOpen(true);
    setForm({ name: "", slug: "", eyebrow: "", description: "" });
    setSlugManual(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="600" color="#333">
          Add Category
        </Typography>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" color="inherit" href="/admin/dashboard">
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" href="/admin/category">
            Category List
          </Link>
          <Typography color="text.primary">Add Category</Typography>
        </Breadcrumbs>
      </Box>

      {/* Card */}
      <Paper elevation={2} sx={{ borderRadius: 3, p: 4, backgroundColor: "#fff" }}>
        <Typography variant="h5" fontWeight="600" mb={4}>
          Create New Category
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Row 1 — Name + Slug */}
          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "260px" }}>
              <Typography variant="subtitle2" fontWeight="600" mb={0.8}>
                Category Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Junction Boxes"
                name="name"
                value={form.name}
                onChange={handleChange}
                size="small"
                error={!!errors.name}
                helperText={errors.name}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: "260px" }}>
              <Typography variant="subtitle2" fontWeight="600" mb={0.8}>
                URL Slug *{" "}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  (auto-generated, editable)
                </Typography>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. junction-boxes"
                name="slug"
                value={form.slug}
                onChange={handleSlugChange}
                size="small"
                error={!!errors.slug}
                helperText={
                  errors.slug ||
                  `Frontstore URL: /${form.slug || "your-slug"}`
                }
              />
            </Box>
          </Box>

          {/* Row 2 — Eyebrow label */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="600" mb={0.8}>
              Eyebrow Label *{" "}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                (small tag shown above the page title, e.g. "Industrial Grade")
              </Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Industrial Grade"
              name="eyebrow"
              value={form.eyebrow}
              onChange={handleChange}
              size="small"
              error={!!errors.eyebrow}
              helperText={errors.eyebrow}
            />
          </Box>

          {/* Row 3 — Description */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="600" mb={0.8}>
              Category Description *{" "}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                (shown as subtitle on the category listing page)
              </Typography>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Short description of this category for customers…"
              name="description"
              value={form.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            sx={{
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontWeight: "600",
              textTransform: "uppercase",
              boxShadow: "none",
            }}
          >
            Add Category
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
      >
        <Alert severity="success" variant="filled">
          Category Added Successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddCategory;